import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usersApi } from '@/api/users.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { AssignmentMatrixModal } from '@/components/users/AssignmentMatrixModal';
import { FaultRosterModal } from '@/components/users/FaultRosterModal';
import { ViewType } from '@/types/domain';
import { ApiError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';
import { useToasts } from '@/context/ToastContext';
import { formatDateTime } from '@/lib/format';
import { VIEW_TYPE_OPTIONS, viewTypeLabel } from '@/lib/labels';
import type {
  CreateUserPayload,
  CreatedUserResponse,
  ResetPinResponse,
  UserRecord
} from '@/types/api';

// Helper: collapse blank/whitespace-only strings to undefined BEFORE running
// the inner schema. Without this, `z.string().optional()` happily accepts
// '' which then trips Length(5,20) on the backend → silent 400 → phone
// "lost" symptom.
const blankToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), schema);

const UserSchema = z.object({
  name: z.string().trim().min(2).max(100),
  alias: z.string().trim().min(2).max(50),
  email: z.string().trim().email(),
  phone: blankToUndefined(z.string().min(5).max(20).optional()),
  role: z.string().trim().min(2).max(50),
  geo: blankToUndefined(z.string().optional()),
  view_type: z.nativeEnum(ViewType),
  // V4.5 — cap raised from 100 → 100,000 so teams handling thousands of
  // tickets can size individual users appropriately (e.g. 2k+ open).
  max_open_tickets: z.coerce.number().int().min(1).max(100000).optional(),
  active: z.boolean().optional(),
  notes: blankToUndefined(z.string().optional()),
  pin: blankToUndefined(z.string().regex(/^\d{6}$/u, 'PIN must be 6 digits').optional()),
  // V4.4 — single-select "Handles" picker. Stored on the wire as the
  // matching array on category_families.
  handles: z.enum(['Problem', 'Sales', 'All']).default('All')
});

type UserFormValues = z.infer<typeof UserSchema>;

export function UsersPage() {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  // V4.3 — gate per-action buttons on the same capabilities the backend
  // enforces. Admin grants any of these via the Permissions matrix.
  const { hasCapability, user: currentUser } = useAuth();
  const canCreateUser = hasCapability('users.create');
  const canUpdateUser = hasCapability('users.update');
  const canResetPin = hasCapability('users.reset_pin');
  // V4.5d — delete capability gates the Delete button.
  const canDeleteUser = hasCapability('users.delete');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserRecord | null>(null);
  const [pinInfo, setPinInfo] = useState<ResetPinResponse | CreatedUserResponse | null>(null);
  // V4.9.15 — admin-only assignment matrix editor (who handles what).
  const [matrixOpen, setMatrixOpen] = useState(false);
  // V4.9.19 — admin-only fault roster editor (who can be marked at fault).
  const [faultRosterOpen, setFaultRosterOpen] = useState(false);
  const isAdmin = currentUser?.view_type === ViewType.Admin;
  // V4.9.18 — "Show deleted users" mode. When off, this page is byte-for-byte
  // identical to before; when on, it lists soft-deleted accounts with a
  // Restore action. Gated on the same users.delete capability.
  const [showDeleted, setShowDeleted] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list()
  });

  // V4.9.18 — soft-deleted users, fetched only when the admin opens the
  // "Show deleted" view (and only if they can delete/restore).
  const deletedUsersQuery = useQuery({
    queryKey: ['users', 'deleted'],
    queryFn: () => usersApi.listDeleted(),
    enabled: showDeleted && canDeleteUser
  });

  const resetPinMutation = useMutation({
    mutationFn: (id: string) => usersApi.resetPin(id),
    onSuccess: (res) => {
      setPinInfo(res);
      push({ kind: 'success', title: 'PIN reset', message: `Share the new PIN with ${res.alias}` });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'PIN reset failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  // V4.5d — Delete user (soft delete) mutation.
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: (res) => {
      push({
        kind: 'success',
        title: `User ${res.alias} deleted`,
        message: res.message
      });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Delete failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  // V4.9.18 — Restore (un-delete) mutation. On success, refresh BOTH the
  // active list and the deleted list so the row moves across immediately.
  const restoreUserMutation = useMutation({
    mutationFn: (id: string) => usersApi.restore(id),
    onSuccess: (res) => {
      push({
        kind: 'success',
        title: `User ${res.alias} restored`,
        message: res.message
      });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Restore failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  return (
    <div>
      <div className="section-header">
        <h2>User management</h2>
        <div className="flex gap-2">
          {canDeleteUser ? (
            <button
              type="button"
              className={`btn btn-sm ${showDeleted ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowDeleted((v) => !v)}
              title="View accounts that were deleted (soft delete) and restore one if it was removed by mistake."
            >
              {showDeleted ? 'Show active users' : 'Show deleted users'}
            </button>
          ) : null}
          {isAdmin ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setMatrixOpen(true)}
              title="Set who is the Solver / Approver / Communicator / Owner per category & region — and edit the country→region mapping."
            >
              Assignment matrix
            </button>
          ) : null}
          {isAdmin ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setFaultRosterOpen(true)}
              title="Manage the people selectable in a ticket's Fault panel (Sales / Designers / Shipment / Vendors)."
            >
              Fault roster
            </button>
          ) : null}
          {canCreateUser ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
              Add user
            </button>
          ) : null}
        </div>
      </div>
      {isAdmin ? (
        <AssignmentMatrixModal open={matrixOpen} onClose={() => setMatrixOpen(false)} />
      ) : null}
      {isAdmin ? (
        <FaultRosterModal open={faultRosterOpen} onClose={() => setFaultRosterOpen(false)} />
      ) : null}

      {!showDeleted ? (
      <div className="card">
        <div className="card-body no-pad">
          {usersQuery.isLoading ? (
            <LoadingState />
          ) : usersQuery.isError ? (
            <ErrorState error={usersQuery.error} onRetry={() => usersQuery.refetch()} />
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Alias</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>View type</th>
                    <th>Geo</th>
                    <th>Handles</th>
                    <th>Max</th>
                    <th>Active</th>
                    <th>Last login</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(usersQuery.data ?? []).map((user) => (
                    <tr key={user.id} className="no-hover">
                      <td>{user.name}</td>
                      <td className="font-mono">{user.alias}</td>
                      <td className="text-small">{user.email}</td>
                      <td className="text-small">{user.role}</td>
                      <td>
                        <Badge tone={viewTypeTone(user.view_type)}>
                          {viewTypeLabel(user.view_type)}
                        </Badge>
                      </td>
                      <td>{user.geo ?? '—'}</td>
                      <td>
                        <Badge tone={handlesTone(user.category_families)}>
                          {handlesLabel(user.category_families)}
                        </Badge>
                      </td>
                      <td>{user.max_open_tickets}</td>
                      <td>
                        {user.active ? (
                          <Badge tone="success">Active</Badge>
                        ) : (
                          <Badge tone="neutral">Inactive</Badge>
                        )}
                      </td>
                      <td className="text-small">
                        {user.last_login_at ? formatDateTime(user.last_login_at) : 'Never'}
                      </td>
                      <td>
                        <div className="row-actions">
                          {canUpdateUser ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditTarget(user)}
                            >
                              Edit
                            </button>
                          ) : null}
                          {canResetPin ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={resetPinMutation.isPending}
                              onClick={() => {
                                if (window.confirm(`Reset PIN for ${user.alias}?`)) {
                                  resetPinMutation.mutate(user.id);
                                }
                              }}
                            >
                              Reset PIN
                            </button>
                          ) : null}
                          {/* V4.5d — Delete (soft delete). Self-delete and
                              last-admin-delete are blocked server-side; we
                              also hide the button if the user is YOU, since
                              attempting it would just bounce. */}
                          {canDeleteUser && currentUser?.sub !== user.id ? (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={deleteUserMutation.isPending}
                              onClick={() => {
                                const ok = window.confirm(
                                  `Delete user ${user.alias} (${user.email})?\n\n` +
                                    `This will:\n` +
                                    ` • mark them as inactive\n` +
                                    ` • revoke any active sessions\n` +
                                    ` • hide them from auto-assignment\n\n` +
                                    `Tickets they own stay; reassign them via the Reassign button on each ticket if needed.\n\n` +
                                    `This is a soft delete — you can undo it any time from "Show deleted users" → Restore.`
                                );
                                if (ok) {
                                  deleteUserMutation.mutate(user.id);
                                }
                              }}
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      ) : (
        <DeletedUsersCard
          query={deletedUsersQuery}
          canRestore={canDeleteUser}
          restorePending={restoreUserMutation.isPending}
          onRestore={(u) => {
            const ok = window.confirm(
              `Restore user ${u.alias} (${u.email})?\n\n` +
                `This will:\n` +
                ` • re-activate the account\n` +
                ` • let them sign in again with their existing PIN\n` +
                ` • bring back their tickets, queue and auto-assignments\n\n` +
                `They'll need to log in fresh — sessions were revoked when the account was deleted.`
            );
            if (ok) {
              restoreUserMutation.mutate(u.id);
            }
          }}
        />
      )}

      <UserFormModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSubmitted={(pin) => {
          void queryClient.invalidateQueries({ queryKey: ['users'] });
          setCreateOpen(false);
          if (pin) {
            setPinInfo(pin);
          }
        }}
      />

      {editTarget ? (
        <UserFormModal
          open={!!editTarget}
          mode="update"
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmitted={() => {
            void queryClient.invalidateQueries({ queryKey: ['users'] });
            setEditTarget(null);
          }}
        />
      ) : null}

      <PinRevealModal info={pinInfo} onClose={() => setPinInfo(null)} />
    </div>
  );
}

// V4.9.18 — Deleted-users view. Rendered only when the admin toggles "Show
// deleted users". Lists soft-deleted accounts with a Restore action. Kept as
// a separate component so the normal (active) Users table is untouched.
function DeletedUsersCard({
  query,
  canRestore,
  restorePending,
  onRestore
}: {
  query: UseQueryResult<UserRecord[]>;
  canRestore: boolean;
  restorePending: boolean;
  onRestore: (user: UserRecord) => void;
}) {
  const rows = query.data ?? [];
  return (
    <div className="card">
      <div className="card-body no-pad">
        {query.isLoading ? (
          <LoadingState />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : rows.length === 0 ? (
          <div className="text-small text-muted" style={{ padding: 24 }}>
            No deleted users. Accounts you delete appear here so you can restore them.
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Alias</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>View type</th>
                  <th>Geo</th>
                  <th>Deleted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.id} className="no-hover">
                    <td>{user.name}</td>
                    <td className="font-mono">{user.alias}</td>
                    <td className="text-small">{user.email}</td>
                    <td className="text-small">{user.role}</td>
                    <td>
                      <Badge tone={viewTypeTone(user.view_type)}>
                        {viewTypeLabel(user.view_type)}
                      </Badge>
                    </td>
                    <td>{user.geo ?? '—'}</td>
                    <td className="text-small">
                      {user.deleted_at ? formatDateTime(user.deleted_at) : '—'}
                    </td>
                    <td>
                      <div className="row-actions">
                        {canRestore ? (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={restorePending}
                            onClick={() => onRestore(user)}
                          >
                            Restore
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface UserFormModalProps {
  open: boolean;
  mode: 'create' | 'update';
  user?: UserRecord;
  onClose: () => void;
  onSubmitted: (pin?: CreatedUserResponse) => void;
}

function UserFormModal({ open, mode, user, onClose, onSubmitted }: UserFormModalProps) {
  const { push } = useToasts();
  const [apiError, setApiError] = useState<string | null>(null);

  const defaults = useMemo<UserFormValues>(() => {
    if (mode === 'update' && user) {
      return {
        name: user.name,
        alias: user.alias,
        email: user.email,
        phone: user.phone ?? undefined,
        role: user.role,
        geo: user.geo ?? undefined,
        view_type: user.view_type,
        max_open_tickets: user.max_open_tickets,
        active: user.active,
        notes: user.notes ?? undefined,
        pin: undefined,
        handles: handlesFromFamilies(user.category_families)
      };
    }
    return {
      name: '',
      alias: '',
      email: '',
      role: '',
      view_type: ViewType.Agent,
      max_open_tickets: 15,
      active: true,
      handles: 'All'
    } as UserFormValues;
  }, [mode, user]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<UserFormValues>({
    resolver: zodResolver(UserSchema),
    defaultValues: defaults
  });

  React.useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  const create = useMutation({
    mutationFn: (payload: CreateUserPayload) => usersApi.create(payload),
    onSuccess: (res) => {
      push({ kind: 'success', title: 'User created' });
      onSubmitted(res);
    },
    onError: (err: unknown) => {
      setApiError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed');
    }
  });

  const update = useMutation({
    mutationFn: (payload: Partial<CreateUserPayload>) => usersApi.update(user!.id, payload),
    onSuccess: () => {
      push({ kind: 'success', title: 'User updated' });
      onSubmitted();
    },
    onError: (err: unknown) => {
      setApiError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed');
    }
  });

  const onSubmit = handleSubmit((values) => {
    setApiError(null);
    const payload: CreateUserPayload = {
      name: values.name,
      alias: values.alias,
      email: values.email,
      phone: values.phone,
      role: values.role,
      geo: values.geo,
      view_type: values.view_type,
      max_open_tickets: values.max_open_tickets,
      active: values.active,
      notes: values.notes,
      pin: values.pin,
      category_families: familiesFromHandles(values.handles)
    };
    if (mode === 'create') {
      create.mutate(payload);
    } else {
      update.mutate(payload);
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={mode === 'create' ? 'Create user' : `Edit user — ${user?.alias}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            form="user-form"
            disabled={isSubmitting || create.isPending || update.isPending}
          >
            {mode === 'create' ? 'Create user' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="user-form" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <Field label="Full name" required error={errors.name?.message ?? null}>
            <input className="form-input" {...register('name')} />
          </Field>
          <Field label="Alias" required error={errors.alias?.message ?? null}>
            <input className="form-input" {...register('alias')} />
          </Field>
          <Field label="Email" required error={errors.email?.message ?? null}>
            <input type="email" className="form-input" {...register('email')} />
          </Field>
          <Field label="Phone" error={errors.phone?.message ?? null}>
            <input className="form-input" {...register('phone')} />
          </Field>
          <Field label="Role title" required error={errors.role?.message ?? null}>
            <input className="form-input" {...register('role')} />
          </Field>
          <Field label="Geo" error={errors.geo?.message ?? null}>
            <input className="form-input" {...register('geo')} placeholder="USA/CA, UK, AUS/NZ, ALL" />
          </Field>
          <Field label="View type" required error={errors.view_type?.message ?? null}>
            <select className="form-select" {...register('view_type')}>
              {VIEW_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Max open tickets" error={errors.max_open_tickets?.message ?? null}>
            <input type="number" min={1} max={100000} className="form-input" {...register('max_open_tickets')} />
          </Field>
          <Field
            label={mode === 'create' ? 'Initial PIN (optional)' : 'New PIN (optional)'}
            hint="Leave blank to auto-generate a PIN when creating a user."
            error={errors.pin?.message ?? null}
          >
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="form-input"
              {...register('pin')}
            />
          </Field>
          <Field label="Active">
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('active')} />
              <span className="text-small">Active user</span>
            </label>
          </Field>
          <Field
            label="Handles category"
            hint="A1–A7 = Problem family (refunds, reprints, replacements, delivery, chargeback). B1–B4 = Sales family (quotation, custom template, product doubt, assembly instruction)."
            error={errors.handles?.message ?? null}
          >
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input type="radio" value="Problem" {...register('handles')} />
                <span className="text-small">A1–A7 — Problem family only</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" value="Sales" {...register('handles')} />
                <span className="text-small">B1–B4 — Sales family only</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" value="All" {...register('handles')} />
                <span className="text-small">All — handles both families</span>
              </label>
            </div>
          </Field>
        </div>
        <Field label="Notes" error={errors.notes?.message ?? null}>
          <textarea className="form-textarea" rows={2} {...register('notes')} />
        </Field>
        {apiError ? <div className="form-error">{apiError}</div> : null}
      </form>
    </Modal>
  );
}

function PinRevealModal({
  info,
  onClose
}: {
  info: ResetPinResponse | CreatedUserResponse | null;
  onClose: () => void;
}) {
  if (!info) return null;
  const pin =
    'temporary_pin' in info
      ? info.temporary_pin
      : 'initial_pin' in info
        ? info.initial_pin
        : undefined;
  const alias =
    'alias' in info ? info.alias : '';
  if (!pin) {
    return null;
  }
  return (
    <Modal
      open={!!info}
      onClose={onClose}
      title="One-time PIN"
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I noted it
        </button>
      }
    >
      <p>Share this PIN with <strong>{alias}</strong> over a secure channel. It won't be shown again.</p>
      <div
        className="font-mono"
        style={{
          fontSize: 32,
          textAlign: 'center',
          padding: 16,
          background: '#f1f5f9',
          borderRadius: 12,
          letterSpacing: 6
        }}
      >
        {pin}
      </div>
      <p className="text-small text-muted mt-3">
        On first login they should change it via the Change PIN page.
      </p>
    </Modal>
  );
}

function viewTypeTone(vt: ViewType) {
  switch (vt) {
    case ViewType.Admin:
      return 'primary' as const;
    case ViewType.Specialist:
      return 'info' as const;
    case ViewType.Communicator:
      return 'success' as const;
    case ViewType.SalesLead:
      return 'warning' as const;
    default:
      return 'neutral' as const;
  }
}

// V4.4 — translate between the UI's 3-way picker (Problem / Sales / All)
// and the backend's array (['Problem'] | ['Sales'] | ['Problem','Sales']).
type HandlesChoice = 'Problem' | 'Sales' | 'All';

function handlesFromFamilies(families: ('Problem' | 'Sales')[] | null | undefined): HandlesChoice {
  const set = new Set(families ?? []);
  if (set.has('Problem') && set.has('Sales')) return 'All';
  if (set.has('Problem')) return 'Problem';
  if (set.has('Sales')) return 'Sales';
  return 'All';
}

function familiesFromHandles(handles: HandlesChoice): ('Problem' | 'Sales')[] {
  if (handles === 'Problem') return ['Problem'];
  if (handles === 'Sales') return ['Sales'];
  return ['Problem', 'Sales'];
}

function handlesLabel(families: ('Problem' | 'Sales')[] | null | undefined): string {
  const choice = handlesFromFamilies(families);
  if (choice === 'Problem') return 'A1–A7';
  if (choice === 'Sales') return 'B1–B4';
  return 'All';
}

function handlesTone(families: ('Problem' | 'Sales')[] | null | undefined) {
  const choice = handlesFromFamilies(families);
  if (choice === 'Problem') return 'info' as const;
  if (choice === 'Sales') return 'warning' as const;
  return 'neutral' as const;
}
